import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

import useDragScroll from 'utils/hooks/useDragScroll';

import './index.scss';

export interface UnderlineTabItem {
  path: string;
  label: string;
  /** Conteúdo depois do rótulo (ex.: StatusDot). */
  trailing?: React.ReactNode;
  /** Separador vertical antes da aba (ex.: "Aprovação"). */
  dividerBefore?: boolean;
}

/**
 * N2 da hierarquia mobile (NAV_HIERARCHY.md): abas de sublinhado para trocar o conteúdo
 * dentro de uma tela. Ativa = `location.pathname === item.path`. Visual e marcação espelham
 * `OscillationTabs` (barra branca rente ao N1, rolagem em x sem barra, arraste com o mouse via
 * `useDragScroll`); acrescenta o conteúdo depois do rótulo (StatusDot) e o divisor. O container
 * traz a aba ativa para a vista quando a rota muda.
 */
export default function UnderlineTabs({
  items,
  ariaLabel,
}: {
  items: UnderlineTabItem[];
  ariaLabel: string;
}) {
  const location = useLocation();
  const activeRef = useRef<HTMLAnchorElement>(null);
  const {
    ref,
    dragHandlers: {
      onPointerDown, onPointerMove, onPointerUp, onPointerLeave, onClickCapture,
    },
  } = useDragScroll<HTMLDivElement>();

  useEffect(() => {
    const el = activeRef.current;
    // jsdom não implementa scrollIntoView.
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ inline: 'center', block: 'nearest' });
    }
  }, [location.pathname]);

  return (
    <nav className="mobile-underline-tabs" aria-label={ariaLabel}>
      <div
        className="mobile-underline-tabs-scroller"
        ref={ref}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerLeave}
        onClickCapture={onClickCapture}
      >
        {items.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <React.Fragment key={item.path}>
              {item.dividerBefore && (
                <span className="mobile-underline-tabs-divider" aria-hidden="true" />
              )}
              <Link
                to={item.path}
                ref={isActive ? activeRef : undefined}
                className={`mobile-underline-tabs-tab${isActive ? ' active' : ''}`}
                aria-current={isActive ? 'page' : undefined}
              >
                {item.label}
                {item.trailing}
              </Link>
            </React.Fragment>
          );
        })}
      </div>
    </nav>
  );
}
